import React from 'react'
import { Button, Dropdown, Space, Divider } from 'antd'
import { 
  MenuOutlined, 
  PlayCircleOutlined, 
  PlusOutlined, 
  SettingOutlined, 
  DollarOutlined, 
  BorderOutlined, 
  SaveOutlined, 
  EyeOutlined, 
  BulbOutlined, 
  AimOutlined, 
  SafetyCertificateOutlined, 
  StarOutlined,
  QuestionCircleOutlined,
  CameraOutlined,
  BlockOutlined,
  PictureOutlined
} from '@ant-design/icons'
import type { MenuProps } from 'antd'

interface HeaderMenuProps {
  hasObjects: boolean
  showInteriorView: boolean
  showMeasurements: boolean
  showSaveLoad: boolean
  showCanvasControls: boolean
  showEnhancedRendering: boolean
  showSmartPlacement: boolean
  showCompliance: boolean
  showDesignIntelligence: boolean
  realisticModeEnabled: boolean
  terrain3DEnabled: boolean
  hasBackgroundImage: boolean
  onStartWizard: () => void
  onAddADU: () => void
  onAddPool: () => void
  onToggleDesignTools: () => void
  onTogglePricing: () => void
  onToggleMeasurements: () => void
  onToggleSaveLoad: () => void
  onToggleCanvasControls: () => void
  onToggleInteriorView: () => void
  onToggleEnhancedRendering: () => void
  onToggleSmartPlacement: () => void
  onToggleCompliance: () => void
  onToggleDesignIntelligence: () => void
  onToggleRealisticMode: () => void
  onToggle3DTerrain: () => void
  onShowImageTransform: () => void
  onShowHelp: () => void
}

const HeaderMenu: React.FC<HeaderMenuProps> = ({
  hasObjects,
  showInteriorView,
  showMeasurements,
  showSaveLoad,
  showCanvasControls,
  showEnhancedRendering,
  showSmartPlacement,
  showCompliance,
  showDesignIntelligence,
  realisticModeEnabled,
  terrain3DEnabled,
  hasBackgroundImage,
  onStartWizard,
  onAddADU,
  onAddPool,
  onToggleDesignTools,
  onTogglePricing,
  onToggleMeasurements,
  onToggleSaveLoad,
  onToggleCanvasControls,
  onToggleInteriorView,
  onToggleEnhancedRendering,
  onToggleSmartPlacement,
  onToggleCompliance,
  onToggleDesignIntelligence,
  onToggleRealisticMode,
  onToggle3DTerrain,
  onShowImageTransform,
  onShowHelp
}) => {
  const menuItems: MenuProps['items'] = [
    // Getting Started Section
    {
      key: 'getting-started',
      label: 'Getting Started',
      type: 'group',
      children: [
        {
          key: 'start-wizard',
          label: (
            <Space>
              <PlayCircleOutlined />
              Start Wizard
            </Space>
          ),
          onClick: onStartWizard
        },
        {
          key: 'add-adu',
          label: (
            <Space>
              <PlusOutlined />
              Add ADU
            </Space>
          ),
          onClick: onAddADU
        },
        {
          key: 'add-pool',
          label: (
            <Space>
              <PlusOutlined />
              Add Pool
            </Space>
          ),
          onClick: onAddPool
        }
      ]
    },
    
    // Design Tools Section (only show if objects exist)
    ...(hasObjects ? [{
      key: 'design-tools',
      label: 'Design Tools',
      type: 'group' as const,
      children: [
        {
          key: 'design-settings',
          label: (
            <Space>
              <SettingOutlined />
              Design Settings
            </Space>
          ),
          onClick: onToggleDesignTools
        },
        {
          key: 'measurements',
          label: (
            <Space>
              <BorderOutlined />
              Measurements
              {showMeasurements && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleMeasurements
        },
        {
          key: 'interior-view',
          label: (
            <Space>
              <EyeOutlined />
              Interior View
              {showInteriorView && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleInteriorView
        }
      ]
    }] : []),

    // Canvas & Rendering Section
    {
      key: 'canvas-rendering',
      label: 'Canvas & Rendering',
      type: 'group',
      children: [
        {
          key: 'canvas-controls',
          label: (
            <Space>
              <SettingOutlined />
              Canvas Settings
              {showCanvasControls && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleCanvasControls
        },
        {
          key: 'lighting-weather',
          label: (
            <Space>
              <BulbOutlined />
              Lighting & Weather
              {showEnhancedRendering && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleEnhancedRendering
        },
        {
          key: 'realistic-mode',
          label: (
            <Space>
              <CameraOutlined />
              Realistic Visualization
              {realisticModeEnabled && <span style={{ color: '#52c41a' }}>●</span>}
            </Space>
          ),
                 onClick: onToggleRealisticMode
               },
               {
                 key: '3d-terrain',
                 label: (
                   <Space>
                     <BlockOutlined />
                     3D Terrain
                     {terrain3DEnabled && <span style={{ color: '#52c41a' }}>●</span>}
                   </Space>
                 ),
                 onClick: onToggle3DTerrain
               },
               ...(hasBackgroundImage ? [{
                 key: 'image-transform',
                 label: (
                   <Space>
                     <PictureOutlined />
                     Transform Image
                   </Space>
                 ),
                 onClick: onShowImageTransform
               }] : [])
             ]
           },

    // AI & Smart Features Section
    {
      key: 'ai-features',
      label: 'AI & Smart Features',
      type: 'group',
      children: [
        {
          key: 'smart-placement',
          label: (
            <Space>
              <AimOutlined />
              Smart Placement
              {showSmartPlacement && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleSmartPlacement
        },
        {
          key: 'compliance',
          label: (
            <Space>
              <SafetyCertificateOutlined />
              Compliance Check
              {showCompliance && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleCompliance
        },
        {
          key: 'design-ai',
          label: (
            <Space>
              <StarOutlined />
              Design AI
              {showDesignIntelligence && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleDesignIntelligence
        }
      ]
    },

    // Project Management Section
    {
      key: 'project-management',
      label: 'Project Management',
      type: 'group',
      children: [
        {
          key: 'save-load',
          label: (
            <Space>
              <SaveOutlined />
              Save/Load Project
              {showSaveLoad && <span style={{ color: '#1890ff' }}>●</span>}
            </Space>
          ),
          onClick: onToggleSaveLoad
        },
        ...(hasObjects ? [{
          key: 'get-quote',
          label: (
            <Space>
              <DollarOutlined />
              Get Quote
            </Space>
          ),
          onClick: onTogglePricing
        }] : [])
      ]
    },

    // Help Section
    {
      key: 'help',
      label: 'Help & Support',
      type: 'group',
      children: [
        {
          key: 'help-guide',
          label: (
            <Space>
              <QuestionCircleOutlined />
              Help & Shortcuts
            </Space>
          ),
          onClick: onShowHelp
        }
      ]
    }
  ]

  const handleMenuClick = (e: any) => {
    // Prevent menu from closing when clicking on panel toggles
    e.domEvent?.stopPropagation()
  }

  return (
    <Dropdown
      menu={{ 
        items: menuItems,
        onClick: handleMenuClick
      }}
      trigger={['click']}
      placement="bottomRight"
      overlayStyle={{ minWidth: '320px' }}
      autoAdjustOverflow={true}
    >
      <Button 
        type="primary" 
        icon={<MenuOutlined />}
        size="large"
        style={{ 
          background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
          border: 'none',
          boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
        }}
      >
        Controls
      </Button>
    </Dropdown>
  )
}

export default HeaderMenu
